/**
 * cdn-domain-header-rules-tab.tsx — Header Rules tab for a CDN domain (act-as).
 *
 * Mirrors Partner's HeaderRulesTab.tsx: DataTable of request/response header
 * manipulation rules with inline add/edit/delete via a modal editor and a simple
 * condition builder (expression textarea — full wirefilter visual editor is A4).
 *
 * Endpoints: GET/PUT/DELETE /api/v3/cdn/domains/:id/header-rules
 *   - PUT sends the full rules array (replace-all semantics).
 *   - DELETE removes the entire config.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNHeaderRules,
  actAsCDNRulesKeys,
  type HeaderRule,
} from '@/api/act-as-cdn-rules';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── Constants ───────────────────────────────────────────────────────────── */

const PHASES = [
  { value: 'request', label: 'Request' },
  { value: 'response', label: 'Response' },
] as const;

const ACTIONS = [
  { value: 'set', label: 'Set' },
  { value: 'append', label: 'Append' },
  { value: 'remove', label: 'Remove' },
] as const;

/* ── Rule modal ──────────────────────────────────────────────────────────── */

function newRule(): HeaderRule {
  return {
    phase: 'request',
    action: 'set',
    header_name: '',
    header_value: '',
    condition: '',
    priority: 10,
  };
}

interface RuleEditorProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: HeaderRule;
  onSave: (rule: HeaderRule) => void;
}

function RuleEditor({ open, onOpenChange, initial, onSave }: RuleEditorProps) {
  const { t } = useTranslation();
  const [rule, setRule] = useState<HeaderRule>(initial ?? newRule());

  useEffect(() => {
    if (open) setRule(initial ?? newRule());
  }, [open, initial]);

  const set = <K extends keyof HeaderRule>(k: K, v: HeaderRule[K]) =>
    setRule((r) => ({ ...r, [k]: v }));

  const valid = rule.header_name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        {initial
          ? t('cdn_domain.header_rules.edit_title', 'Edit header rule')
          : t('cdn_domain.header_rules.add_title', 'Add header rule')}
      </DialogHeader>
      <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: '1fr 1fr' }}>
          <Field label={t('cdn_domain.header_rules.field_phase', 'Phase')}>
            {(props) => (
              <Select value={rule.phase} onValueChange={(v) => set('phase', v as HeaderRule['phase'])}>
                <SelectTrigger {...props}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PHASES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label={t('cdn_domain.header_rules.field_action', 'Action')}>
            {(props) => (
              <Select value={rule.action} onValueChange={(v) => set('action', v as HeaderRule['action'])}>
                <SelectTrigger {...props}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
        <Field
          label={t('cdn_domain.header_rules.field_header_name', 'Header name')}
          description={t('cdn_domain.header_rules.field_header_name_desc', 'e.g. X-Custom-Header')}
        >
          {(props) => (
            <Input
              {...props}
              value={rule.header_name}
              placeholder="X-Custom-Header"
              onChange={(e) => set('header_name', e.target.value)}
            />
          )}
        </Field>
        {rule.action !== 'remove' && (
          <Field label={t('cdn_domain.header_rules.field_header_value', 'Header value')}>
            {(props) => (
              <Input
                {...props}
                value={rule.header_value ?? ''}
                placeholder="my-value"
                onChange={(e) => set('header_value', e.target.value)}
              />
            )}
          </Field>
        )}
        <Field
          label={t('cdn_domain.header_rules.field_priority', 'Priority')}
          description={t('cdn_domain.header_rules.field_priority_desc', 'Lower number = evaluated first.')}
        >
          {(props) => (
            <NumericInput
              {...props}
              value={String(rule.priority)}
              onChange={(e) => set('priority', Number(e.target.value) || 0)}
              style={{ maxWidth: 120 }}
            />
          )}
        </Field>
        <Field
          label={t('cdn_domain.header_rules.field_condition', 'Condition (optional)')}
          description={t(
            'cdn_domain.header_rules.field_condition_desc',
            'Wirefilter expression — leave empty to apply to all requests.',
          )}
        >
          {(props) => (
            <Textarea
              {...props}
              value={rule.condition ?? ''}
              placeholder='http.request.uri.path matches "^/api/"'
              rows={3}
              onChange={(e) => set('condition', e.target.value)}
            />
          )}
        </Field>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button disabled={!valid} onClick={() => { if (valid) { onSave(rule); onOpenChange(false); } }}>
          {t('common.save', 'Save')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface CdnDomainHeaderRulesTabProps {
  domainId: string;
}

export function CdnDomainHeaderRulesTab({ domainId }: CdnDomainHeaderRulesTabProps) {
  const { t } = useTranslation();
  const q = useActAsCDNHeaderRules(domainId);

  const [rules, setRules] = useState<HeaderRule[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    setRules(q.data?.rules ?? []);
  }, [q.data]);

  const openAdd = () => { setEditingIdx(null); setEditorOpen(true); };
  const openEdit = (idx: number) => { setEditingIdx(idx); setEditorOpen(true); };

  const handleSave = (rule: HeaderRule) => {
    setRules((prev) => {
      if (editingIdx !== null) {
        return prev.map((r, i) => (i === editingIdx ? rule : r));
      }
      return [...prev, rule];
    });
  };

  const handleRemove = (idx: number) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const payload = { rules };

  if (q.isLoading) return <Skeleton style={{ height: 240 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.header_rules.load_error', 'Failed to load header rules')}
        onRetry={() => q.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.header_rules.title', 'Header Rules')}
      description={t(
        'cdn_domain.header_rules.description',
        'Add, modify, or remove HTTP request and response headers at the edge.',
      )}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={openAdd}
              >
                {t('cdn_domain.header_rules.add_rule', 'Add rule')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.header_rules.rules_title', 'Rules')}</CardTitle>
            <CardDescription>
              {t(
                'cdn_domain.header_rules.rules_desc',
                'Rules are evaluated in priority order (ascending). Lower number = first.',
              )}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {rules.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Tag size={20} />}
                title={t('cdn_domain.header_rules.empty_title', 'No header rules yet')}
                description={t(
                  'cdn_domain.header_rules.empty_desc',
                  'Add a rule to manipulate request or response headers at the edge.',
                )}
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {/* Column headers */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 80px 1fr 1fr auto',
                    gap: 'var(--vn-space-2)',
                    padding: '0 var(--vn-space-2)',
                    fontSize: 'var(--vn-font-size-xs)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--vn-fg-subtle)',
                  }}
                >
                  <span>{t('cdn_domain.header_rules.col_phase', 'Phase')}</span>
                  <span>{t('cdn_domain.header_rules.col_action', 'Action')}</span>
                  <span>{t('cdn_domain.header_rules.col_header', 'Header')}</span>
                  <span>{t('cdn_domain.header_rules.col_condition', 'Condition')}</span>
                  <span />
                </div>
                {rules.map((rule, idx) => (
                  <Card
                    flat
                    key={idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 80px 1fr 1fr auto',
                      gap: 'var(--vn-space-2)',
                      alignItems: 'center',
                      padding: 'var(--vn-space-2)',
                    }}
                  >
                    <Badge tone="info" appearance="subtle" size="sm">
                      {rule.phase}
                    </Badge>
                    <Badge
                      tone={rule.action === 'remove' ? 'danger' : 'neutral'}
                      appearance="subtle"
                      size="sm"
                    >
                      {rule.action}
                    </Badge>
                    <span
                      style={{
                        fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)',
                        fontSize: 'var(--vn-font-size-sm)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {rule.header_name}
                      {rule.header_value ? (
                        <span style={{ color: 'var(--vn-fg-muted)' }}>: {rule.header_value}</span>
                      ) : null}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--vn-font-size-sm)',
                        color: 'var(--vn-fg-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {rule.condition || '—'}
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.edit', 'Edit')}
                        onClick={() => openEdit(idx)}
                      >
                        <Pencil size={14} />
                      </IconButton>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.remove', 'Remove')}
                        onClick={() => handleRemove(idx)}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            q.data ? (
              <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
                {t('cdn_domain.header_rules.action_remove', 'Remove config')}
              </Button>
            ) : undefined
          }
          end={
            <Button onClick={() => setConfirmOpen(true)}>
              {t('cdn_domain.header_rules.action_save', 'Save')}
            </Button>
          }
        />
      </div>

      <RuleEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editingIdx !== null ? rules[editingIdx] : undefined}
        onSave={handleSave}
      />

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.header_rules.confirm_save_title', 'Save header rules')}
        summary={t(
          'cdn_domain.header_rules.confirm_save_summary',
          '{{n}} header rule(s) will be applied at the edge.',
          { n: rules.length },
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.headerRules(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/header-rules`, payload)}
      />

      <ActAsMutationConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('cdn_domain.header_rules.confirm_delete_title', 'Remove header rules config')}
        summary={t(
          'cdn_domain.header_rules.confirm_delete_summary',
          'All header rules will be removed. The edge will stop manipulating headers for this domain.',
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.headerRules(domainId)]}
        mutationFn={() => actAsClient.delete(`/cdn/domains/${domainId}/header-rules`)}
        onSuccess={() => setRules([])}
        destructive
        confirmLabel={t('cdn_domain.header_rules.action_remove', 'Remove config')}
      />
    </Section>
  );
}
